import { PANEL_PHOTOS } from './registration'

export type ArchiveKind = 'photograph' | 'audio' | 'memory' | 'artifact' | 'record'

export interface ArchiveItem {
  id: string
  year: number
  kind: ArchiveKind
  title: string
  /** Optional media. Records and audio routinely have none. */
  image?: { src: string; alt: string }
  /** Audio only. */
  duration?: string
  /** Audio only — a line from the transcript, so it is readable without playing. */
  transcript?: string
  /** Who is speaking, for audio. */
  speaker?: string
  /** Memory only — the account itself. */
  quote?: string
  /** How the contributor chose to be credited, or the seeding source. */
  credit: string
  /** Set when a photograph or video has unidentified people in it. */
  unidentified?: number
  /** Moderation state. Only shown to whoever submitted the item. */
  status?: 'review' | 'live'
  /** True when the signed-in member submitted this. */
  mine?: boolean
  note?: string
}

export const ARCHIVE_KIND_LABELS: Record<ArchiveKind, string> = {
  photograph: 'Photograph',
  audio: 'Recording',
  memory: 'Memory',
  artifact: 'Artifact',
  record: 'Record',
}

const MEDIA = {
  flyer: {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/08a8ffbf-d48b-41ec-b831-cc556c4b096a.jpg',
    alt: 'A photocopied Halloween party flyer on orange paper, taped to a painted brick wall',
  },

  newsletter: {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/ad4e1d7b-37b3-4e2b-8249-e8966ebb913d.jpg',
    alt: 'A scanned 1980s community newsletter lying flat, typewritten columns and a grainy photo',
  },
  van: {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/3980f3ca-5307-4e88-add0-a790adeb5fb2.jpg',
    alt: 'An old passenger van parked outside a low brick building, bags being loaded',
  },
  meals: {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/0ffc0035-3f91-4886-98ca-9cdb950e38bb.jpg',
    alt: 'Volunteers serving food from folding tables in a parking lot under a canopy',
  },
}

export const ARCHIVE_ITEMS: ArchiveItem[] = [
  {
    id: 'a-1978-first-meeting',
    year: 1978,
    kind: 'record',
    title: 'The first meeting, in a borrowed church basement',
    credit: 'Seeded by staff',
    note: 'Three nights a week, no titles, one kettle.',
  },
  {
    id: 'a-1979-basement',
    year: 1979,
    kind: 'photograph',
    title: 'Folding tables in the basement drop-in room',
    image: PANEL_PHOTOS.founding,
    credit: 'From Marion A.',
    unidentified: 6,
  },
  {
    id: 'a-1981-storefront',
    year: 1981,
    kind: 'photograph',
    title: 'The Oak Street storefront, first winter',
    image: PANEL_PHOTOS.building,
    credit: 'Seeded by volunteers',
  },
  {
    id: 'a-1984-newsletter',
    year: 1984,
    kind: 'artifact',
    title: 'Spring newsletter, mailed to 200 households',
    image: MEDIA.newsletter,
    credit: 'From the office files',
    note: 'Two pages, typewritten, with the camp schedule on the back.',
  },
  {
    id: 'a-1986-van',
    year: 1986,
    kind: 'photograph',
    title: 'The camp van, loading for the first summer trip',
    image: MEDIA.van,
    credit: 'From Dev R.',
    unidentified: 4,
  },
  {
    id: 'a-1989-back-room',
    year: 1989,
    kind: 'memory',
    title: 'On the back room',
    quote:
      'The back room saved my life twice. Once in 1989 and once about four years later, when I came back and nobody made anything of it.',
    credit: 'Dev R.',
  },
  {
    id: 'a-1994-housing',
    year: 1994,
    kind: 'record',
    title: 'Housing support opens on Third',
    credit: 'Seeded by staff',
  },
  {
    id: 'a-1996-tuesday',
    year: 1996,
    kind: 'photograph',
    title: 'Tuesday night group, winter',
    image: PANEL_PHOTOS.era,
    credit: 'From the office files',
    unidentified: 9,
  },
  {
    id: 'a-1997-asking',
    year: 1997,
    kind: 'memory',
    title: 'On learning to ask',
    quote:
      'I learned to ask for help in that building. It took me the better part of two years and one very patient person on a Tuesday.',
    credit: 'Theresa M.',
  },
  {
    id: 'a-1998-group-tape',
    year: 1998,
    kind: 'audio',
    title: 'Twentieth anniversary, recorded in the group room',
    duration: '4:12',
    speaker: 'Several voices, unidentified',
    transcript:
      '“…and somebody said, well, we’ve got the room until nine, so we may as well keep going.”',
    credit: 'From a cassette, digitised 2019',
    unidentified: 5,
  },
  {
    id: 'a-1999-chairs',
    year: 1999,
    kind: 'photograph',
    title: 'The room before everyone arrived',
    image: PANEL_PHOTOS.room,
    credit: 'From Ray M.',
    mine: true,
    status: 'live',
  },
  {
    id: 'a-1996-doorway-memory',
    year: 1996,
    kind: 'memory',
    title: 'On the Tuesday doorway',
    quote:
      'Sean stood in that doorway every week until the last person was inside. I used to arrive late on purpose so he’d still be standing there.',
    credit: 'Ray M.',
    mine: true,
    status: 'review',
  },
  {
    id: 'a-2004-mentors',
    year: 2004,
    kind: 'record',
    title: 'First alumni return as mentors',
    credit: 'Seeded by staff',
  },
  {
    id: 'a-2008-coffee',
    year: 2008,
    kind: 'photograph',
    title: 'Morning table, Third Street',
    image: PANEL_PHOTOS.hands,
    credit: 'From Andre P.',
  },
  {
    id: 'a-2012-halloween',
    year: 2012,
    kind: 'artifact',
    title: 'Halloween party flyer',
    image: MEDIA.flyer,
    credit: 'From Nadia K.',
    note: 'Taped by the side door for most of October.',
  },
  {
    id: 'a-2013-second-site',
    year: 2013,
    kind: 'record',
    title: 'Second site opens across the river',
    credit: 'Seeded by staff',
  },
  {
    id: 'a-2019-window',
    year: 2019,
    kind: 'audio',
    title: 'Elena on the first year of the second site',
    image: PANEL_PHOTOS.voices,
    duration: '2:38',
    speaker: 'Elena V., staff 2015–2019',
    transcript:
      '“We had forty chairs and no idea whether anyone would come across the river for us.”',
    credit: 'Recorded at the 2019 reunion',
  },
  {
    id: 'a-1991-doorway',
    year: 1991,
    kind: 'audio',
    title: 'Sean on the Third Street doorway',
    duration: '0:37',
    speaker: 'Sean D., staff 1991–1995',
    transcript:
      '“You could tell how someone’s week had gone by how long they stood in the doorway before coming in.”',
    credit: 'Recorded at the 2019 reunion',
  },
  {
    id: 'a-2022-meals',
    year: 2022,
    kind: 'photograph',
    title: 'Meals in the parking lot',
    image: MEDIA.meals,
    credit: 'From Sam O.',
    unidentified: 3,
  },
]

export function decadeOf(year: number): string {
  return `${Math.floor(year / 10) * 10}s`
}

export const FIRST_YEAR = 1976
export const LAST_YEAR = 2026

/**
 * One source of truth for every count shown anywhere, derived from the items
 * themselves. Nothing on the landing page is allowed to assert a number that
 * this function cannot produce.
 */
export function archiveStats() {
  const byKind = ARCHIVE_ITEMS.reduce<Record<ArchiveKind, number>>(
    (counts, item) => {
      counts[item.kind] += 1
      return counts
    },
    { photograph: 0, audio: 0, memory: 0, artifact: 0, record: 0 },
  )

  return {
    total: ARCHIVE_ITEMS.length,
    byKind,
    unnamed: ARCHIVE_ITEMS.reduce((sum, item) => sum + (item.unidentified ?? 0), 0),
    yearsCovered: new Set(ARCHIVE_ITEMS.map((item) => item.year)).size,
    yearsTotal: LAST_YEAR - FIRST_YEAR,
  }
}

export function itemsInYears(start: number, end: number): ArchiveItem[] {
  return ARCHIVE_ITEMS.filter((item) => item.year >= start && item.year <= end)
}

/** Years inside a span that hold almost nothing. */
export function thinYears(start: number, end: number): { year: number; count: number }[] {
  const years: { year: number; count: number }[] = []
  for (let year = start; year <= end; year += 1) {
    const count = ARCHIVE_ITEMS.filter((item) => item.year === year).length
    if (count <= 1) years.push({ year, count })
  }
  return years
}
